import React, { useState, useEffect, useRef } from 'react';
import './OnboardingTour.css';

const TOUR_STEPS = [
  {
    target: '.input-section',
    title: 'Create Your Tasks 📝',
    content: 'Start by describing what needs to be done. You can assign a priority, date, and beautiful tags right from here.',
  },
  {
    target: '.board-controls',
    title: 'Organize & Filter 🎛️',
    content: 'Click here to manage your custom color tags, or filter the board so you only see what matters right now.',
  },
  {
    target: '.task-box-container > .task-box:nth-child(1)',
    title: 'Drag and Drop 🔄',
    content: 'Your workflow is entirely visual. Grab a task and drag it to In Progress or Completed as you finish your work.',
  },
  {
    target: '.settings-nav-tab',
    title: 'Settings & More ⚙️',
    content: 'Configure your default priority, set your timezone, and update your password by heading over to your settings. Click Finish to complete the tour!',
  }
];

function OnboardingTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const overlayRef = useRef(null);

  const step = TOUR_STEPS[currentStep];

  useEffect(() => {
    const updatePosition = () => {
      if (!step) return;
      const el = document.querySelector(step.target);
      if (el) {
        // Adding a slight padding to the highlight box
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        });
        
        // Ensure element is visible
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    updatePosition();

    // Re-calculate on resize
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [currentStep, step]);

  if (!step || !targetRect) return null;

  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(curr => curr + 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  // Determine tooltip placement (below if enough space, else above)
  const windowHeight = window.innerHeight;
  const placeAbove = targetRect.top + targetRect.height + 200 > windowHeight;

  const tooltipStyle = {
    top: placeAbove ? targetRect.top - 16 : targetRect.top + targetRect.height + 16,
    left: Math.max(20, Math.min(targetRect.left, window.innerWidth - 340)), // Keep within screen bounds
    transform: placeAbove ? 'translateY(-100%)' : 'none'
  };

  return (
    <div className="onboarding-overlay" ref={overlayRef}>
      {/* SVG Spotlight */}
      <svg className="onboarding-spotlight-svg" preserveAspectRatio="none">
        <defs>
          <mask id="spotlight-mask">
            {/* White covers entire screen (opaque) */}
            <rect width="100%" height="100%" fill="white" />
            {/* Black punches a glowing hole for the target */}
            <rect 
              x={targetRect.left} 
              y={targetRect.top} 
              width={targetRect.width} 
              height={targetRect.height} 
              rx="12" 
              fill="black" 
              className="spotlight-hole"
            />
          </mask>
        </defs>
        {/* Render a dark semi-transparent rectangle that applies the mask */}
        <rect width="100%" height="100%" fill="rgba(0, 5, 15, 0.75)" mask="url(#spotlight-mask)" />
      </svg>

      {/* Pulsing glow behind the highlighted target */}
      <div 
        className="onboarding-highlight-glow"
        style={{
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
        }}
      />

      {/* Tooltip Card */}
      <div className="onboarding-tooltip" style={tooltipStyle}>
        <div className="onboarding-progress">
          {TOUR_STEPS.map((_, i) => (
            <div key={i} className={`onboarding-dot ${i === currentStep ? 'active' : ''}`} />
          ))}
        </div>
        
        <h3 className="onboarding-title">{step.title}</h3>
        <p className="onboarding-content">{step.content}</p>
        
        <div className="onboarding-actions">
          <button className="onboarding-btn skip" onClick={handleSkip}>
            Skip Tour
          </button>
          <button className="onboarding-btn next" onClick={handleNext}>
            {isLastStep ? 'Finish ✨' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
